"use client"

import { useEffect, useState } from "react"
import { motion } from "motion/react"

interface Heading {
  id: string
  text: string
  level: number
}

interface GuideTOCProps {
  headings: Heading[]
}

export function GuideTOC({ headings }: GuideTOCProps) {
  const [activeId, setActiveId] = useState<string>("")

  // Scrollspy logic
  useEffect(() => {
    if (headings.length === 0) return

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id)
          }
        })
      },
      { rootMargin: "-20% 0% -80% 0%" }
    )

    headings.forEach((heading) => {
      const el = document.getElementById(heading.id)
      if (el) observer.observe(el)
    })

    return () => observer.disconnect()
  }, [headings])

  if (headings.length === 0) return null

  return (
    <nav className="sticky top-32 max-h-[calc(100vh-10rem)] overflow-y-auto no-scrollbar hidden lg:block pr-6">
      <div className="flex items-center gap-2 mb-6">
        <div className="w-1.5 h-1.5 rounded-full bg-premium-violet/50" />
        <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 font-mono">
          Index
        </p>
      </div>
      <ul className="space-y-4 border-l border-white/5 pl-4 relative">
        {headings.map((heading, i) => {
          const isActive = activeId === heading.id
          
          return (
            <li 
              key={i}
              className={`${heading.level === 3 ? "ml-4" : ""} relative`}
            >
              {isActive && (
                <motion.div
                  layoutId="toc-indicator"
                  className="absolute left-[-17px] w-0.5 bg-premium-cyan h-4 rounded-full shadow-[0_0_10px_rgba(8,145,178,0.8)]"
                  initial={false}
                  transition={{ type: "spring", stiffness: 300, damping: 30 }}
                />
              )}
              <a
                href={`#${heading.id}`}
                className={`block text-[13px] leading-snug transition-all duration-300 ${
                  isActive
                    ? "text-white font-medium drop-shadow-md"
                    : "text-slate-500 hover:text-slate-300"
                }`}
                onClick={(e) => {
                  e.preventDefault()
                  document.getElementById(heading.id)?.scrollIntoView({
                    behavior: "smooth",
                  })
                }}
              >
                {heading.text}
              </a>
            </li>
          )
        })}
      </ul>
    </nav>
  )
}
